import React, { useEffect, useRef, useMemo } from 'react'

interface Props {
    width: number
    height: number
    imageUrl: string
    color: string // hex or rgba
    factionId: number // 1, 2, 3
}

interface Point {
    x: number
    y: number
}

interface Particle {
    x: number
    y: number
    vx: number
    vy: number
    life: number
    maxLife: number
    size: number
    color: string
}

const ZoneBorderParticles: React.FC<Props> = ({ width, height, imageUrl, color, factionId }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const edgePointsRef = useRef<Point[]>([])
    const particlesRef = useRef<Particle[]>([])
    const requestRef = useRef<number | null>(null)
    const imageLoadedRef = useRef<string | null>(null)
    const lastTsRef = useRef(0)

    // Colors based on faction for particle variation
    const getParticleColor = () => {
        if (factionId === 2) { // Chaotic - Red/Orange/Dark
            return Math.random() > 0.5 ? 'rgba(220, 20, 60, ' : 'rgba(255, 69, 0, '
        } else if (factionId === 1) { // Harmonized - Blue/Cyan
            return Math.random() > 0.5 ? 'rgba(56, 189, 248, ' : 'rgba(0, 255, 255, '
        } else { // Untouched - White/Silver
            return Math.random() > 0.5 ? 'rgba(255, 255, 255, ' : 'rgba(200, 200, 255, '
        }
    }

    useEffect(() => {
        // Clear old particles when configuration changes to prevent color mixing
        particlesRef.current = []

        const canvas = canvasRef.current
        if (!canvas) return

        const ctx = canvas.getContext('2d')
        if (!ctx) return

        // 1. Load image and detect edges
        if (imageLoadedRef.current !== imageUrl) {
            const img = new Image()
            img.crossOrigin = "Anonymous"
            img.src = imageUrl
            img.onload = () => {
                // Draw to temp canvas to read data
                const tempCanvas = document.createElement('canvas')
                tempCanvas.width = width
                tempCanvas.height = height
                const tempCtx = tempCanvas.getContext('2d')
                if (!tempCtx) return

                // Draw scaled to fit the zone dimensions
                tempCtx.drawImage(img, 0, 0, width, height)
                const imageData = tempCtx.getImageData(0, 0, width, height)
                const data = imageData.data
                const points: Point[] = []

                // Detect edges
                // Simple algorithm: pixel is not transparent, but has a transparent neighbor
                const threshold = 20
                for (let y = 0; y < height; y += 2) { // Skip pixels for performance
                    for (let x = 0; x < width; x += 2) {
                        const i = (y * width + x) * 4
                        const alpha = data[i + 3]

                        if (alpha > threshold) {
                            // Check neighbors
                            let isEdge = false
                            // Check top, bottom, left, right (roughly)
                            const neighbors = [
                                ((y - 2) * width + x) * 4 + 3,
                                ((y + 2) * width + x) * 4 + 3,
                                (y * width + (x - 2)) * 4 + 3,
                                (y * width + (x + 2)) * 4 + 3
                            ]

                            for (const ni of neighbors) {
                                if (data[ni] === undefined || data[ni] < threshold) {
                                    isEdge = true
                                    break
                                }
                            }

                            if (isEdge) {
                                points.push({ x, y })
                            }
                        }
                    }
                }
                edgePointsRef.current = points
                imageLoadedRef.current = imageUrl
            }
        }

        // 2. Animation Loop
        const animate = (ts: number) => {
            if (!canvas || !ctx) return
            const dtMs = lastTsRef.current === 0 ? 0 : Math.min(ts - lastTsRef.current, 50)
            lastTsRef.current = ts
            const df = dtMs / 16.67

            ctx.clearRect(0, 0, width, height)

            ctx.globalCompositeOperation = 'lighter'

            if (edgePointsRef.current.length > 0) {
                const spawnCount = Math.round(4 * df)
                for (let i = 0; i < spawnCount; i++) {
                    const pt = edgePointsRef.current[Math.floor(Math.random() * edgePointsRef.current.length)]
                    if (pt) {
                        particlesRef.current.push({
                            x: pt.x,
                            y: pt.y,
                            vx: (Math.random() - 0.5) * 0.5,
                            vy: (Math.random() - 0.5) * 0.5 - 0.5, // Slight upward drift
                            life: 1.0,
                            maxLife: 1.0 + Math.random() * 0.5,
                            size: 1 + Math.random() * 2,
                            color: getParticleColor()
                        })
                    }
                }
            }

            // Update and draw particles
            for (let i = particlesRef.current.length - 1; i >= 0; i--) {
                const p = particlesRef.current[i]
                p.x += p.vx * df
                p.y += p.vy * df
                p.life -= 0.02 * df

                if (p.life <= 0) {
                    particlesRef.current.splice(i, 1)
                    continue
                }

                ctx.beginPath()
                const alpha = p.life // Fade out
                ctx.fillStyle = p.color + alpha + ')'
                ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2)
                ctx.fill()
            }

            requestRef.current = requestAnimationFrame(animate)
        }

        requestRef.current = requestAnimationFrame(animate)

        return () => {
            if (requestRef.current) cancelAnimationFrame(requestRef.current)
        }
    }, [width, height, imageUrl, color, factionId])

    return (
        <canvas
            ref={canvasRef}
            width={width}
            height={height}
            className="absolute inset-0 pointer-events-none z-30"
        />
    )
}

export default ZoneBorderParticles
